import type { FC } from "react";

import { SWReactIcons } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";

import { SkeletonFavicon } from "./SkeletonFavIcon";
import {
    Card,
    Title,
    Divider,
    Subtitle,
    Explanation,
    NewsIconWrapper,
    NewsItemTitle,
    NewsItemSnippet,
    NewsSourceContainer,
    NewsTitleContainer,
    Pill,
} from "./StyledComponents";
import type { ClusterExplainationAnalysisDataResponse } from "./types";

interface IDemandSpikeClusterExplainationProps {
    data: ClusterExplainationAnalysisDataResponse;
}

function extractDomain(url: string): string | null {
    try {
        const { hostname } = new URL(url);
        return hostname.replace(/^www\./, ""); // remove www. if present
    } catch (error) {
        console.error("Invalid URL:", url);
        return null;
    }
}

const placeholderFavicon = "https://via.placeholder.com/16";

const DemandSpikeClusterExplanation: FC<IDemandSpikeClusterExplainationProps> = ({ data }) => {
    const i18nBase = "demand.analysis.spike.cluster.explanation.";
    const domains = data?.sources
        ? Array.from(
              new Set(
                  data.sources
                      .map((source) => extractDomain(source.url))
                      .filter((domain): domain is string => !!domain),
              ),
          )
        : [];
    const { data: favicons = {} } = useWebsitesFavicons(domains, undefined, {
        enabled: domains.length > 0,
    });

    return (
        <Card>
            <Title>{translate(`${i18nBase}title`, { cluster: data.theme?.name })}</Title>
            <Subtitle>{translate(`${i18nBase}subtitle`)}</Subtitle>
            <Divider />
            <Explanation>{data.summary}</Explanation>
            <div>
                {data.sources?.map((source) => {
                    const domain = extractDomain(source.url);
                    const faviconUrl = domain ? favicons[domain] : undefined;
                    return (
                        <NewsSourceContainer key={source.title}>
                            <NewsTitleContainer>
                                <NewsIconWrapper>
                                    <SWReactIcons iconName="news" />
                                </NewsIconWrapper>
                                <NewsItemTitle>{source.title}</NewsItemTitle>
                            </NewsTitleContainer>
                            <div>
                                <NewsItemSnippet>{source.snippet}</NewsItemSnippet>
                            </div>
                            {domain ? (
                                <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Pill>
                                        <SkeletonFavicon
                                            src={faviconUrl}
                                            fallbackSrc={placeholderFavicon}
                                            alt={`${domain} favicon`}
                                            size={16}
                                        />
                                        <span style={{ marginLeft: "4px" }}>{domain}</span>
                                    </Pill>
                                </a>
                            ) : null}
                        </NewsSourceContainer>
                    );
                })}
            </div>
        </Card>
    );
};

export default DemandSpikeClusterExplanation;
